import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Clock, Mail, MessageCircle, Upload, CheckCircle2, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { EftPaymentPanel } from "@/components/site/EftPaymentPanel";
import { site } from "@/lib/site";

type Search = {
  ref?: string;
  amount?: number;
  email?: string;
  name?: string;
};

export const Route = createFileRoute("/eft-pending")({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    ref: typeof raw.ref === "string" ? raw.ref : undefined,
    amount: raw.amount != null ? Number(raw.amount) : undefined,
    email: typeof raw.email === "string" ? raw.email : undefined,
    name: typeof raw.name === "string" ? raw.name : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Payment received — pending release | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "Your EFT payment is being verified. Send through your proof of payment via email or WhatsApp and we'll release your notes the same day.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EftPendingPage,
});

function EftPendingPage() {
  const { ref, amount, email, name } = useSearch({ from: "/eft-pending" });
  const reference = ref || generateRef();
  const amountZar = Number.isFinite(amount) && (amount as number) > 0 ? (amount as number) : 0;

  const subject = `Proof of payment — ${reference}`;
  const body = useMemo(
    () =>
      [
        `Hi Nkadimeng Danny Legal team,`,
        ``,
        `Please find attached my proof of payment for order ${reference}.`,
        ``,
        `Name: ${name ?? ""}`,
        `Email: ${email ?? ""}`,
        `Amount paid: R${amountZar}`,
        `Reference used: ${reference}`,
        ``,
        `Kindly release my study notes once the payment reflects. Thank you.`,
      ].join("\n"),
    [reference, name, email, amountZar],
  );

  const mailHref = `mailto:${site.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const waHref = `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(
    `Hi, I've just paid R${amountZar} by EFT. Reference: ${reference}. Attaching proof of payment. Name: ${name ?? ""}, Email: ${email ?? ""}.`,
  )}`;

  return (
    <section className="container-narrow py-12">
      <Link to="/checkout" className="inline-flex items-center gap-1 text-sm text-navy hover:text-gold">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to checkout
      </Link>

      <div className="mt-4 rounded-2xl border border-gold/40 bg-gradient-to-br from-secondary/40 to-background p-6 shadow-soft">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-gold/15 text-gold">
            <Clock className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl text-navy md:text-3xl">
              Payment received — pending release
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Thank you{name ? `, ${name}` : ""}. Your EFT order has been logged. We'll release your
              notes the moment we confirm the deposit.
            </p>
          </div>
        </div>

        <dl className="mt-5 grid gap-3 sm:grid-cols-3">
          <Stat label="Order reference" value={reference} />
          <Stat label="Amount due" value={`R${amountZar}`} />
          <Stat label="Typical release time" value="Same day (within 4 business hours)" />
        </dl>

        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <strong>Important:</strong> please use <span className="font-mono">{reference}</span> as
          your payment reference and send us your proof of payment using either method below. Without
          a proof of payment we cannot release the notes.
        </p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg text-navy">1. Banking details</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Pay this exact amount into the Capitec business account below.
          </p>
          <div className="mt-3">
            <EftPaymentPanel amountZar={amountZar} reference={reference} />
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg text-navy">2. Send your proof of payment</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Attach the bank confirmation (PDF, JPG or PNG) and submit via email or WhatsApp — we
            accept either.
          </p>
          <ProofUploadForm
            mailHref={mailHref}
            waHref={waHref}
            reference={reference}
            email={email}
          />
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg text-navy">What happens next</h2>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
          <Step n={1} title="We verify your deposit">
            Capitec → Capitec reflects instantly. Other banks usually clear within a few hours
            during business days.
          </Step>
          <Step n={2} title="We prepare and email your notes">
            You'll receive a download link at{" "}
            <span className="font-medium text-navy">{email ?? "your registered email"}</span> the
            same day.
          </Step>
          <Step n={3} title="Anything wrong?">
            Reply to the confirmation email or WhatsApp {site.phone} and a real human will help —
            usually within minutes.
          </Step>
        </ol>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words font-mono text-sm font-semibold text-navy">{value}</dd>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gold/15 text-xs font-semibold text-gold">
        {n}
      </span>
      <div>
        <p className="font-medium text-navy">{title}</p>
        <p>{children}</p>
      </div>
    </li>
  );
}

function ProofUploadForm({
  mailHref,
  waHref,
  reference,
  email,
}: {
  mailHref: string;
  waHref: string;
  reference: string;
  email?: string;
}) {
  const [file, setFile] = useState<File | null>(null);

  function pickedLabel() {
    if (!file) return "No file selected yet";
    const kb = Math.max(1, Math.round(file.size / 1024));
    return `${file.name} • ${kb} KB`;
  }

  function handleEmail() {
    if (!file) {
      toast.info("Pick your proof of payment first, then attach it in the email window.");
    }
    window.location.href = mailHref;
  }

  async function handleWhatsApp() {
    if (file && (navigator as any).canShare?.({ files: [file] })) {
      try {
        await (navigator as any).share({
          files: [file],
          title: `Proof of payment — ${reference}`,
          text: `Proof of payment for order ${reference}.`,
        });
        toast.success("Share sheet opened — choose WhatsApp to send your proof.");
        return;
      } catch {
        // fall through to wa.me
      }
    }
    window.open(waHref, "_blank", "noopener");
    if (file) {
      toast.info("WhatsApp opened in a new tab — attach the file you selected here.");
    }
  }

  return (
    <div className="mt-4 space-y-4">
      <label className="block">
        <span className="text-sm font-medium text-navy">Attach proof of payment</span>
        <div className="mt-1.5 flex items-center gap-3 rounded-md border border-dashed border-border bg-background px-3 py-3">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {pickedLabel()}
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-secondary px-2.5 py-1.5 text-xs font-medium text-navy hover:bg-secondary/70">
            <Upload className="h-3.5 w-3.5" />
            {file ? "Change file" : "Choose file"}
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f && f.size > 10 * 1024 * 1024) {
                  toast.error("File is larger than 10MB. Please send a smaller version.");
                  return;
                }
                setFile(f);
                if (f) toast.success("File ready — now choose Email or WhatsApp.");
              }}
            />
          </label>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          PDF, JPG or PNG up to 10MB. Selecting a file here lets WhatsApp share it directly on
          supported devices.
        </p>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={handleEmail}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-navy bg-navy px-4 py-2.5 text-sm font-semibold text-navy-foreground transition hover:bg-navy/90"
        >
          <Mail className="h-4 w-4" /> Email proof
        </button>
        <button
          type="button"
          onClick={handleWhatsApp}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-gold bg-gold/10 px-4 py-2.5 text-sm font-semibold text-navy transition hover:bg-gold/20"
        >
          <MessageCircle className="h-4 w-4" /> Send on WhatsApp
        </button>
      </div>

      <div className="rounded-md bg-secondary/40 px-3 py-2 text-[11px] text-muted-foreground">
        <p className="flex items-start gap-1.5">
          <CheckCircle2 className="mt-0.5 h-3 w-3 text-gold" />
          Reference <span className="font-mono text-navy">{reference}</span> is pre-filled in both
          messages, so you only need to attach the proof.
        </p>
        {email && (
          <p className="mt-1 flex items-start gap-1.5">
            <CheckCircle2 className="mt-0.5 h-3 w-3 text-gold" />
            We'll email your notes back to{" "}
            <span className="font-medium text-navy">{email}</span>.
          </p>
        )}
      </div>
    </div>
  );
}

function generateRef() {
  const stamp = Date.now().toString(36).toUpperCase().slice(-6);
  return `NDL-EFT-${stamp}`;
}
